// packages/storefront/src/components/general/CurrentUrlCopyBox.tsx
import { Box, IconButton, Stack, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { LightTooltip } from '@tourtask/ui/components/LightTooltip';
import { useLocalization } from '@tourtask/storefront/hooks/useLocalization';
import { useCurrentUrlCopy } from '@tourtask/storefront/components/general/useCurrentUrlCopy';

/**
 * Shows the current page URL in a monospace box with a copy-to-clipboard
 * button. Shared by the boot-time config-error surfaces
 * (`OriginNotAllowedAlert`, `RouteNotFound`) so a partner can hand the
 * exact failing URL to TourTask support without retyping it.
 *
 * The button's tooltip is `LightTooltip`, MUI Tooltip's look and timings
 * without Popper: in the reviews widget this box was the only MUI Tooltip,
 * and Popper the largest part of what it cost it. The terms widget draws
 * its own copy of this box without MUI, on the same `useCurrentUrlCopy`.
 */
export function CurrentUrlCopyBox() {
  const { getStr } = useLocalization();
  const { url, copied, copy } = useCurrentUrlCopy();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        backgroundColor: 'action.hover',
        borderRadius: 1,
        px: 1,
        py: 0.5,
      }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          {url}
        </Typography>
      </Box>
      <LightTooltip title={getStr(copied ? 'Copied_to_clipboard' : 'Copy_to_clipboard')}>
        <IconButton
          size="small"
          onClick={copy}
          aria-label={getStr('Copy_to_clipboard')}
        >
          {copied
            ? <CheckIcon fontSize="small" />
            : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </LightTooltip>
    </Stack>
  );
}
